import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { legacyMonthlyDetailEntries, legacyMonthlyFilterGroups, legacyMonthlyOffices, type MonthlyFilterKey } from '../../mock/legacyMonthlySchedule'
import { defaultMonthlyFilters, LegacyMonthlyFilter, type MonthlyFilters } from './LegacyMonthlyFilter'
import './LegacyMonthlyScheduleDetail.css'

type DetailGroupKey = 'other' | 'photo' | 'work' | 'tm'

const detailGroups: Array<{ key: DetailGroupKey; label: string }> = [
  { key: 'other', label: '출산예정일,생일,돌잔치' },
  { key: 'photo', label: '촬영스케줄' },
  { key: 'work', label: '업무스케줄' },
  { key: 'tm', label: 'TM스케줄' },
]

const dataQueryKeys: Array<[string, string]> = [
  ['photo', 'photo_yn'], ['normal', 'normal_yn'], ['work', 'work_yn'], ['tm', 'tm_yn'],
  ['due', 'baby_duedate_yn'], ['birthday', 'baby_birth_yn'], ['party', 'baby_birthparty_day_yn'],
  ['memo', 'memo_yn'], ['trip', 'business_trip_yn'],
]

function cloneFilters(filters: MonthlyFilters): MonthlyFilters {
  return Object.fromEntries(Object.entries(filters).map(([key, values]) => [key, [...values]])) as MonthlyFilters
}

function parseFilters(search = window.location.search): MonthlyFilters {
  const params = new URLSearchParams(search)
  if (params.size === 0 || (!params.has('shoot_status') && params.get('is_first') === 'Y')) return cloneFilters(defaultMonthlyFilters)
  const split = (key: string) => (params.get(key) ?? '').split(',').filter(Boolean)
  const tmStatusRaw = split('tm_status')
  const tmStatus = tmStatusRaw.filter((value) => value !== '0' && value !== '1')
  if (tmStatusRaw.includes('0') || tmStatusRaw.includes('1')) tmStatus.splice(tmStatus.includes('tm_status_all') ? 1 : 0, 0, '0,1')
  return {
    shootStatus: split('shoot_status'),
    contractType: split('contract_type'),
    tmCategory: split('tm_category'),
    tmStatus,
    data: dataQueryKeys.filter(([, queryKey]) => params.get(queryKey) === 'Y').map(([id]) => id),
  }
}

function filterQuery(filters: MonthlyFilters) {
  const tmStatus = filters.tmStatus.flatMap((value) => value === '0,1' ? ['0', '1'] : [value])
  const pairs: Array<[string, string]> = [
    ['shoot_status', filters.shootStatus.join(',')],
    ['tm_category', filters.tmCategory.join(',')],
    ['tm_status', tmStatus.join(',')],
    ['contract_type', filters.contractType.join(',')],
    ...dataQueryKeys.map(([id, queryKey]) => [queryKey, filters.data.includes(id) ? 'Y' : ''] as [string, string]),
  ]
  const first = new URLSearchParams(window.location.search).get('is_first')
  if (first) pairs.push(['is_first', first])
  return pairs.map(([key, value]) => `${key}=${value}`).join('&')
}

function initialRoute() {
  const path = window.location.pathname.split('/').filter(Boolean)
  const day = path.at(-1) ?? '01'
  const month = path.at(-2) ?? '08'
  const year = path.at(-3) ?? '2026'
  const office = path.at(-4) ?? 'mock-office'
  return { date: `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`, office }
}

function detailGroup(category: (typeof legacyMonthlyDetailEntries)[number]['category']): DetailGroupKey {
  return category
}

function validDate(value: string) {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!matched) return false
  const [, year, month, day] = matched.map(Number)
  const parsed = new Date(year, month - 1, day)
  return parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day
}

function moveToDetail(office: string, date: string, preserveQuery = true) {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (!matched) return
  const [, year, month, day] = matched
  window.location.assign(`/v1/schedule/monthly/${encodeURIComponent(office)}/${year}/${month}/${day}/${preserveQuery ? window.location.search : ''}`)
}

export function LegacyMonthlyScheduleDetail() {
  const initial = useMemo(initialRoute, [])
  const [date, setDate] = useState(initial.date)
  const [office, setOffice] = useState(initial.office)
  const [appliedFilters, setAppliedFilters] = useState<MonthlyFilters>(parseFilters)
  const [draftFilters, setDraftFilters] = useState<MonthlyFilters>(() => cloneFilters(parseFilters()))
  const [filterOpen, setFilterOpen] = useState(false)
  const [officeOpen, setOfficeOpen] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [active, setActive] = useState<string | null>(null)
  const [iframeSrc, setIframeSrc] = useState('about:blank')
  const officeRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const closeOffice = (event: MouseEvent) => {
      if (!officeRef.current?.contains(event.target as Node)) setOfficeOpen(false)
    }
    document.addEventListener('mousedown', closeOffice)
    return () => document.removeEventListener('mousedown', closeOffice)
  }, [])

  useEffect(() => {
    if (new URLSearchParams(window.location.search).size > 0) return
    window.history.replaceState({}, '', `${window.location.pathname}?${filterQuery(appliedFilters)}`)
  }, [appliedFilters])

  const rows = useMemo(
    () => legacyMonthlyDetailEntries.filter((entry) => {
      if (!entry.label.toLowerCase().includes(searchTerm.toLowerCase())) return false
      if (!appliedFilters.data.includes(entry.dataKey)) return false
      if (entry.shootStatus && !appliedFilters.shootStatus.includes(entry.shootStatus)) return false
      if (entry.contractType && !appliedFilters.contractType.includes(entry.contractType)) return false
      if (entry.tmCategory && !appliedFilters.tmCategory.includes(entry.tmCategory)) return false
      if (entry.tmStatus && !appliedFilters.tmStatus.includes(entry.tmStatus)) return false
      return true
    }),
    [appliedFilters, searchTerm],
  )

  const groupedRows = useMemo(() => detailGroups.map((group) => ({
    ...group,
    rows: rows.filter((row) => detailGroup(row.category) === group.key),
  })).filter((group) => group.rows.length > 0), [rows])

  const weekday = useMemo(() => {
    const [year, month, day] = date.split('-').map(Number)
    if (!year || !month || !day) return ''
    return ['일', '월', '화', '수', '목', '금', '토'][new Date(year, month - 1, day).getDay()]
  }, [date])

  const toggleFilter = (key: MonthlyFilterKey, id: string) => {
    setDraftFilters((current) => {
      const next = cloneFilters(current)
      if (id === 'all') {
        if (key === 'data') {
          const full = ['photo', 'normal', 'work', 'tm', 'due', 'birthday', 'party', 'memo']
          const turnOn = !full.every((item) => current.data.includes(item))
          next.data = turnOn ? [...full, ...(current.data.includes('trip') ? ['trip'] : [])] : current.data.filter((item) => item === 'trip')
          next.tmCategory = turnOn ? legacyMonthlyFilterGroups.find((group) => group.key === 'tmCategory')!.options.map((item) => item.id) : []
          next.tmStatus = turnOn ? legacyMonthlyFilterGroups.find((group) => group.key === 'tmStatus')!.options.map((item) => item.id) : []
          return next
        }
        const options = legacyMonthlyFilterGroups.find((group) => group.key === key)!.options.filter((item) => item.id !== 'all' && !item.disabled).map((item) => item.id)
        next[key] = options.every((item) => current[key].includes(item)) ? [] : options
        if ((key === 'tmCategory' || key === 'tmStatus') && next[key].length) next.data = [...new Set([...next.data, 'tm'])]
        return next
      }
      next[key] = current[key].includes(id) ? current[key].filter((item) => item !== id) : [...current[key], id]
      if (key === 'tmCategory' || key === 'tmStatus') {
        const hasTmChild = (key === 'tmCategory' ? next.tmCategory : next.tmStatus).length > 0
        next.data = hasTmChild ? [...new Set([...next.data, 'tm'])] : (next.tmCategory.length || next.tmStatus.length ? next.data : next.data.filter((item) => item !== 'tm'))
      }
      return next
    })
  }

  const applyFilters = () => {
    const next = cloneFilters(draftFilters)
    const visibleAfterApply = legacyMonthlyDetailEntries.filter((entry) => {
      if (!entry.label.toLowerCase().includes(searchTerm.toLowerCase())) return false
      if (!next.data.includes(entry.dataKey)) return false
      if (entry.shootStatus && !next.shootStatus.includes(entry.shootStatus)) return false
      if (entry.contractType && !next.contractType.includes(entry.contractType)) return false
      if (entry.tmCategory && !next.tmCategory.includes(entry.tmCategory)) return false
      if (entry.tmStatus && !next.tmStatus.includes(entry.tmStatus)) return false
      return true
    }).map((entry) => entry.id)
    setAppliedFilters(next)
    setSelected((current) => current.filter((id) => visibleAfterApply.includes(id)))
    window.history.replaceState({}, '', `${window.location.pathname}?${filterQuery(next)}`)
    setFilterOpen(false)
  }

  const runSearch = () => {
    setSearchTerm(keyword)
    setSelected([])
    setActive(null)
  }

  const searchOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    runSearch()
  }

  const visibleIds = rows.map((row) => row.id)
  const selectedOffice = legacyMonthlyOffices.find((item) => item.id === office) ?? legacyMonthlyOffices[0]
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id))
  const toggleAll = () => setSelected((current) => allVisibleSelected
    ? current.filter((id) => !visibleIds.includes(id))
    : [...new Set([...current, ...visibleIds])])
  const toggleRow = (id: string) => setSelected((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id])
  const toggleGroup = (ids: string[]) => setSelected((current) => ids.every((id) => current.includes(id))
    ? current.filter((id) => !ids.includes(id))
    : [...new Set([...current, ...ids])])

  const openCustomer = (id: string, customerId: string) => {
    setActive(id)
    setIframeSrc(`/v1/customer/?CRUD=2&id=${encodeURIComponent(customerId)}&caller=detail`)
  }

  const openSms = () => {
    if (selected.length === 0) return
    window.open('/v1/sms_send/?un_limit=1&caller=monthly_detail', '_blank', 'width=900,height=1000,top=50,left=50,location=no,status=no')
  }

  return <div className="g_content_wrap legacy-s02-detail-page">
    <div className="g_title">월스케줄세부</div>
    <div className="s02-detail-toolbar g_row gap_10 g_input_wrap">
      <label className="g_label title center" htmlFor="l_sel_year_month">일자</label>
      <input
        id="l_sel_year_month"
        className="g_input g_input_datepicker s02-detail-date"
        value={date}
        onChange={(event) => {
          const nextDate = event.target.value
          setDate(nextDate)
          if (validDate(nextDate) && nextDate !== initial.date) moveToDetail(office, nextDate)
        }}
        pattern="^\d{4}-\d{2}-\d{2}$"
      />
      <label className="g_label title center" htmlFor="l_sel_office">지점</label>
      <select id="l_sel_office" className="g_input chosen-select width_2x s02-detail-office-native" value={office} onChange={(event) => {
        const nextOffice = event.target.value
        setOffice(nextOffice)
        moveToDetail(nextOffice, date, false)
      }}>
        {legacyMonthlyOffices.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}
      </select>
      <div id="l_sel_office_chosen" className={`chosen-container chosen-container-single s02-detail-office-chosen${officeOpen ? ' chosen-with-drop' : ''}`} ref={officeRef}>
        <button className="chosen-single" type="button" onClick={() => setOfficeOpen((open) => !open)}><span>{selectedOffice.name}</span><b /></button>
        {officeOpen && <div className="chosen-drop"><ul className="chosen-results">{legacyMonthlyOffices.map((item) => <li key={item.id} className={item.id === office ? 'result-selected' : 'active-result'}><button type="button" onClick={() => {
          setOffice(item.id)
          setOfficeOpen(false)
          moveToDetail(item.id, date, false)
        }}>{item.name}</button></li>)}</ul></div>}
      </div>
      <LegacyMonthlyFilter open={filterOpen} filters={draftFilters} onOpen={() => { setDraftFilters(cloneFilters(appliedFilters)); setFilterOpen(true) }} onClose={() => { setDraftFilters(cloneFilters(appliedFilters)); setFilterOpen(false) }} onApply={applyFilters} onToggle={toggleFilter} />
    </div>
    <div className="s02-detail-inner-main inner_main">
      <section className="s02-detail-list detail_list g_scroll_bar">
        <div className="s02-detail-search-row g_input_wrap g_row"><label className="g_label title center" htmlFor="l_keyword">검색어</label><input className="g_input" id="l_keyword" value={keyword} onChange={(event) => setKeyword(event.target.value)} onKeyDown={searchOnEnter} /><button id="l_searchBtn" className="g_btn search size_1" type="button" onClick={runSearch}>검색</button></div>
        <div className="s02-detail-search-row g_input_wrap g_row"><span className="g_label title center">조회결과</span><span className="l_searchCnt">조회: {rows.length} / 전체: {legacyMonthlyDetailEntries.length}</span></div>
        <div className="s02-detail-selection-row g_row">
          <div className="l_all_check"><input id="l_data_all_check" type="checkbox" checked={allVisibleSelected} onChange={toggleAll} /><label htmlFor="l_data_all_check">전체 선택</label></div>
          <span className="l_chkSmsCnt">{selected.length}</span><span>명 선택</span><button id="sendSMSButton" className="g_btn g_btn_submit size_1 sendSMSButton" type="button" onClick={openSms}>SMS 전송</button>
        </div>
        <div className="s02-detail-day-heading days"><div className="day">{weekday}</div></div>
        <div id="day_data_list" className="s02-detail-day-data dates g_scroll_bar">
          <div className="l_totalDivEl">
            {groupedRows.map((group) => {
              const groupIds = group.rows.map((row) => row.id)
              const groupSelected = groupIds.length > 0 && groupIds.every((id) => selected.includes(id))
              return <div className="s02-detail-group" key={group.key}>
                <div className="l_filterd_schdule_title">
                  <input id={`l_${group.key}_all_chk`} className="l_toggle_checkbox_all" type="checkbox" checked={groupSelected} onChange={() => toggleGroup(groupIds)} />
                  <label htmlFor={`l_${group.key}_all_chk`}><span>{group.label}</span></label>
                </div>
                {group.rows.map((row) => <div className={`s02-detail-data-row l_${group.key}DivEl ${active === row.id ? 'active' : ''}`} key={row.id}>
                  <input className="sms_input l_toggle_checkbox" type="checkbox" aria-label="Mock 일정 문자 선택" checked={selected.includes(row.id)} onChange={() => toggleRow(row.id)} />
                  <button className="s02-detail-data-link" type="button" style={{ color: row.color }} onClick={() => openCustomer(row.id, row.customerId)}>{row.label}</button>
                </div>)}
              </div>
            })}
          </div>
        </div>
      </section>
      <section className="s02-detail-customer contract_dom flex_wrap_start l_detailCustomer" aria-label="Mock 고객 정보">
        <iframe className="l_iframe" src={iframeSrc} width="100%" height="100%" frameBorder={0} title="Mock 고객 상세" />
      </section>
    </div>
  </div>
}
